import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { PropertyRepository } from '../handling_data/repositories/PropertyRepository';
import { PropertyService } from '../handling_data/services/PropertyService';

export interface UploadTask {
    id: string;
    fileName: string;
    progress: number;
    status: 'uploading' | 'success' | 'error';
    error?: string;
    propertyId: number;
    files: File[];
    category: string;
    deleteIds?: number[];
}

interface UploadContextType {
    tasks: UploadTask[];
    uploadFiles: (propertyId: number, files: File[], category: string, deleteIds?: number[]) => Promise<void>;
    retryTask: (id: string) => Promise<void>;
    replaceFileAndRetry: (id: string, file: File) => Promise<void>;
    removeTask: (id: string) => void;
    clearCompleted: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [tasks, setTasks] = useState<UploadTask[]>([]);

    const updateTask = useCallback((id: string, updates: Partial<UploadTask>) => {
        setTasks(prev => prev.map(task => task.id === id ? { ...task, ...updates } : task));
    }, []);

    const removeTask = useCallback((id: string) => {
        setTasks(prev => prev.filter(task => task.id !== id));
    }, []);

    const clearCompleted = useCallback(() => {
        setTasks(prev => prev.filter(task => task.status === 'uploading'));
    }, []);

    // Safety: Warn user if they try to refresh/leave during active upload
    React.useEffect(() => {
        const hasActiveUploads = tasks.some(t => t.status === 'uploading');

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasActiveUploads) {
                e.preventDefault();
                e.returnValue = ''; // Required by modern browsers
                return '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [tasks]);

    const performUpload = useCallback(async (task: UploadTask) => {
        updateTask(task.id, { status: 'uploading', progress: 0, error: undefined });

        try {
            await PropertyService.uploadMedia(task.propertyId, task.files, task.category, task.deleteIds);

            updateTask(task.id, { status: 'success', progress: 100 });

            // Dispatch global event for list refresh
            window.dispatchEvent(new CustomEvent('property-media-updated', {
                detail: { propertyId: task.propertyId }
            }));
        } catch (err: any) {
            updateTask(task.id, {
                status: 'error',
                error: err.response?.data?.message || 'Upload failed'
            });
        }
    }, [updateTask]);

    const uploadFiles = async (propertyId: number, files: File[], category: string, deleteIds?: number[]) => {
        if (files.length === 0 && (!deleteIds || deleteIds.length === 0)) return;

        const taskId = `${propertyId}-${Date.now()}`;
        const mainName = files.length > 0 ? files[0].name : `Media Update (${propertyId})`;
        const displayName = files.length > 1 ? `${mainName} (+${files.length - 1} ${category === 'property_image' ? 'صور' : 'ملفات'})` : mainName;

        const newTask: UploadTask = {
            id: taskId,
            fileName: displayName,
            progress: 0,
            status: 'uploading',
            propertyId,
            files: files,
            category,
            deleteIds: deleteIds
        };

        setTasks(prev => [newTask, ...prev]);
        performUpload(newTask);
    };

    const retryTask = async (id: string) => {
        const task = tasks.find(t => t.id === id);
        if (task && task.status === 'error') {
            await performUpload(task);
        }
    };

    const replaceFileAndRetry = async (id: string, file: File) => {
        const task = tasks.find(t => t.id === id);
        if (task && task.status === 'error') {
            // Update the task with new file info (replacing the whole batch with this one for now, or we could be smarter)
            const updatedTask = { ...task, files: [file], fileName: file.name };
            updateTask(id, { files: [file], fileName: file.name });
            await performUpload(updatedTask);
        }
    };

    return (
        <UploadContext.Provider value={{ tasks, uploadFiles, retryTask, replaceFileAndRetry, removeTask, clearCompleted }}>
            {children}
        </UploadContext.Provider>
    );
};

export const useUpload = () => {
    const context = useContext(UploadContext);
    if (!context) throw new Error('useUpload must be used within UploadProvider');
    return context;
};
